import {
  accountPaths,
  Build,
  BuildStatus,
  ReleaseActor,
} from '@todesktop/shared';

import { DeepPartial } from '../types';
import { getData, getSnapshot, setData } from './common';
import { firestore } from './firestore';
import {
  collection,
  deleteField,
  doc,
  DocumentSnapshot,
  FirestoreError,
  getCountFromServer,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  Query,
  query,
  QueryDocumentSnapshot,
  QuerySnapshot,
  startAfter,
  where,
  writeBatch,
} from 'firebase/firestore';

const getApplicationBuilds = (id: string, appId: string) =>
  collection(firestore, accountPaths.builds(id, appId));

const getApplicationBuildData = (
  userId: string,
  appId: string,
  buildId: string,
) => doc(firestore, accountPaths.build(userId, appId, buildId));

export const getBuildData = (userId: string, appId: string, buildId: string) =>
  getData<Build>(getApplicationBuildData(userId, appId, buildId));

export const setBuildData = (
  userId: string,
  appId: string,
  buildId: string,
  data: DeepPartial<Build>,
) =>
  setData<DeepPartial<Build>>(
    getApplicationBuildData(userId, appId, buildId),
    data,
    { merge: true },
  );

/**
 * Every `createdAt` starts with the date, so it sorts by time. That includes
 * the legacy format `parseDate` reads, like `2020-04-16[1587048656603]...`.
 * Builds without a `createdAt` are left out, as they always have been.
 */
const newestFirst = () => orderBy('createdAt', 'desc');

/** Listens to the newest builds of an app */
export const onRecentBuildsUpdate = (
  userId: string,
  appId: string,
  size: number,
  handler: (snapshot: QuerySnapshot<Build>) => void,
  onError?: (error: FirestoreError) => void,
) =>
  onSnapshot(
    query(
      getApplicationBuilds(userId, appId),
      newestFirst(),
      limit(size),
    ) as Query<Build>,
    handler,
    onError,
  );

/** Gets the page of builds that follows `after` */
export const getOlderBuilds = (
  userId: string,
  appId: string,
  after: QueryDocumentSnapshot<Build>,
  size: number,
) =>
  getDocs(
    query(
      getApplicationBuilds(userId, appId),
      newestFirst(),
      startAfter(after),
      limit(size),
    ) as Query<Build>,
  );

// Firestore's limit on writes per batch
const MAX_BATCH_WRITES = 500;

/**
 * Marks builds as ones the user won't release, or undoes that when
 * `dismissal` is omitted.
 */
export const setBuildsReleaseDismissed = async (
  userId: string,
  appId: string,
  buildIds: string[],
  dismissal?: { at: string; by: ReleaseActor },
) => {
  const data = dismissal
    ? { releaseDismissedAt: dismissal.at, releaseDismissedBy: dismissal.by }
    : { releaseDismissedAt: deleteField(), releaseDismissedBy: deleteField() };
  const batches = [];
  for (let i = 0; i < buildIds.length; i += MAX_BATCH_WRITES) {
    const batch = writeBatch(firestore);
    for (const buildId of buildIds.slice(i, i + MAX_BATCH_WRITES)) {
      batch.update(getApplicationBuildData(userId, appId, buildId), data);
    }
    batches.push(batch.commit());
  }
  await Promise.all(batches);
};

/** Gets every build of an app version */
export const getBuildsOfVersion = async (
  userId: string,
  appId: string,
  appVersion: string,
) => {
  const snapshot = await getDocs(
    query(
      getApplicationBuilds(userId, appId),
      where('appVersion', '==', appVersion),
    ),
  );
  return snapshot.docs.map((document) => document.data() as Build);
};

/** Counts the builds that can be listed, which all have a `createdAt` */
export const getBuildCount = async (userId: string, appId: string) =>
  (
    await getCountFromServer(
      query(getApplicationBuilds(userId, appId), orderBy('createdAt')),
    )
  ).data().count;

/** Counts the builds created after `createdAt` */
export const getBuildCountCreatedAfter = async (
  userId: string,
  appId: string,
  createdAt: string,
) =>
  (
    await getCountFromServer(
      query(
        getApplicationBuilds(userId, appId),
        where('createdAt', '>', createdAt),
      ),
    )
  ).data().count;

/** Gets the builds created in a range of ISO dates (inclusive) */
export const getBuildsCreatedBetween = async (
  userId: string,
  appId: string,
  start: string,
  end: string,
) => {
  const snapshot = await getDocs(
    query(
      getApplicationBuilds(userId, appId),
      where('createdAt', '>=', start),
      where('createdAt', '<=', end),
      newestFirst(),
    ),
  );
  return snapshot.docs.map((document) => document.data() as Build);
};

/**
 * Gets the builds last fully or partially released in a range of ISO dates
 * (inclusive)
 */
export const getBuildsReleasedBetween = async (
  userId: string,
  appId: string,
  field: 'partiallyReleasedAt' | 'releasedAt',
  start: string,
  end: string,
) => {
  const snapshot = await getDocs(
    query(
      getApplicationBuilds(userId, appId),
      where(field, '>=', start),
      where(field, '<=', end),
    ),
  );
  return snapshot.docs.map((document) => document.data() as Build);
};

/** Gets the most recently released builds */
export const getRecentlyReleasedBuilds = async (
  userId: string,
  appId: string,
  size: number,
) => {
  const snapshot = await getDocs(
    query(
      getApplicationBuilds(userId, appId),
      orderBy('releasedAt', 'desc'),
      limit(size),
    ),
  );
  return snapshot.docs.map((document) => document.data() as Build);
};

/** Gets the newest successful build */
export const getLatestSucceededBuild = async (
  userId: string,
  appId: string,
) => {
  const snapshot = await getDocs(
    query(
      getApplicationBuilds(userId, appId),
      where('status', '==', BuildStatus.succeeded),
      newestFirst(),
      limit(1),
    ),
  );
  return snapshot.docs[0]?.data() as Build | undefined;
};

/** Gets the build released most recently before an ISO date */
export const getLatestReleaseBefore = async (
  userId: string,
  appId: string,
  before: string,
) => {
  const snapshot = await getDocs(
    query(
      getApplicationBuilds(userId, appId),
      where('releasedAt', '<', before),
      orderBy('releasedAt', 'desc'),
      limit(1),
    ),
  );
  return snapshot.docs[0]?.data() as Build | undefined;
};

export const onBuildUpdate = (
  userId: string,
  buildId: string,
  appId: string,
  handler: (arg: Build, snapshot?: DocumentSnapshot) => void,
  onError?: (error: FirestoreError) => void,
) =>
  getSnapshot<Build>(
    getApplicationBuildData(userId, appId, buildId),
    handler,
    onError,
  );
